import React from 'react';
import { Heart, Send, Users, Eye } from 'lucide-react';
import { motion } from 'framer-motion';

interface ConnectionNavigationBarProps {
  activeTab: "sent" | "received" | "matched" | "views";
  onTabChange: (tab: "sent" | "received" | "matched" | "views") => void;
  counts: {
    sent: number;
    received: number;
    matched: number;
    views: number;
  };
  likeLimits?: {
    remaining: number;
    isUnlimited: boolean;
  };
}

const ConnectionNavigationBar: React.FC<ConnectionNavigationBarProps> = ({
  activeTab,
  onTabChange,
  counts,
  likeLimits
}) => {
  const tabs = [
    {
      id: "received" as const,
      label: "Recebidas",
      labelShort: "Rec.",
      icon: Heart,
      count: counts.received,
      gradient: "from-pink-500 to-rose-500"
    },
    {
      id: "sent" as const,
      label: "Enviadas", 
      labelShort: "Env.",
      icon: Send,
      count: counts.sent,
      gradient: "from-blue-500 to-indigo-500"
    },
    {
      id: "matched" as const,
      label: "Matches",
      labelShort: "Match",
      icon: Users,
      count: counts.matched,
      gradient: "from-emerald-500 to-teal-500"
    },
    {
      id: "views" as const,
      label: "Visualizaram",
      labelShort: "Views",
      icon: Eye,
      count: counts.views,
      gradient: "from-amber-500 to-orange-500"
    }
  ];

  const getLimitsDisplay = () => {
    // Don't show limits for unlimited users or if no limits data
    if (!likeLimits || likeLimits.isUnlimited) return null;
    
    let colorClass = 'text-gray-300';
    let icon = '💫';
    
    if (likeLimits.remaining <= 1) {
      colorClass = 'text-red-400 animate-pulse';
      icon = '⚠️';
    } else if (likeLimits.remaining <= 3) {
      colorClass = 'text-yellow-400';
      icon = '🔥';
    }
    
    return { 
      text: `${likeLimits.remaining} curtidas restantes`, 
      colorClass, 
      icon 
    };
  };

  const limitsDisplay = getLimitsDisplay();

  return (
    <div className="w-full bg-gradient-to-br from-black via-black to-gray-900 pt-16 sm:pt-20">
      {/* Navigation Tabs */}
      <div className="px-2 sm:px-4 py-4 sm:py-6">
        <div className="grid grid-cols-4 gap-1.5 sm:gap-3 max-w-4xl mx-auto">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            const Icon = tab.icon;
            
            return (
              <motion.button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={`
                  relative overflow-hidden rounded-xl sm:rounded-2xl p-2 sm:p-4 transition-all duration-300
                  ${isActive 
                    ? 'bg-white/10 border-2 border-white/20 shadow-2xl' 
                    : 'bg-white/5 border border-white/10 hover:bg-white/8 hover:border-white/15'
                  }
                `}
                whileHover={{ scale: isActive ? 1 : 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {/* Gradient Background for Active Tab */}
                {isActive && (
                  <div className={`absolute inset-0 bg-gradient-to-br ${tab.gradient} opacity-10`} />
                )}
                
                <div className="relative flex flex-col items-center space-y-1 sm:space-y-2">
                  {/* Icon */}
                  <div className={`
                    p-1.5 sm:p-2 rounded-lg sm:rounded-xl transition-colors duration-300
                    ${isActive 
                      ? `bg-gradient-to-br ${tab.gradient} text-white shadow-lg` 
                      : 'bg-white/10 text-white/70'
                    }
                  `}>
                    <Icon className="h-3.5 w-3.5 sm:h-5 sm:w-5" />
                  </div>
                  
                  {/* Label */}
                  <span className={`
                    text-xs sm:text-sm font-medium transition-colors duration-300 text-center leading-tight
                    ${isActive ? 'text-white' : 'text-white/70'}
                  `}>
                    <span className="block sm:hidden">{tab.labelShort}</span>
                    <span className="hidden sm:block">{tab.label}</span>
                  </span>
                  
                  {/* Count Badge */}
                  <div className={`
                    min-w-[20px] sm:min-w-[24px] h-5 sm:h-6 px-1.5 sm:px-2 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300
                    ${isActive 
                      ? `bg-gradient-to-r ${tab.gradient} text-white shadow-lg` 
                      : 'bg-white/20 text-white/80'
                    }
                  `}>
                    {tab.count}
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Like Limits Indicator - Only show for users with limits and on received tab */}
      {limitsDisplay && activeTab === 'received' && (
        <div className="px-4 pb-4 sm:pb-6">
          <div className="max-w-4xl mx-auto">
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`
                inline-flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-2 sm:py-3 rounded-xl sm:rounded-2xl text-xs sm:text-sm font-medium
                bg-gradient-to-r from-gray-800/50 to-gray-700/50 backdrop-blur-sm
                border border-white/10 ${limitsDisplay.colorClass} transition-all duration-300
              `}
            >
              <span className="text-base sm:text-lg">{limitsDisplay.icon}</span>
              <Heart className="h-3 w-3 sm:h-4 sm:w-4" />
              <span>{limitsDisplay.text}</span>
            </motion.div>
          </div>
        </div>
      )}

      {/* Bottom Border */}
      <div className="h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
    </div>
  );
};

export default ConnectionNavigationBar;
